/**
 * Numeric tunables. Every number in DESIGN.md is canonical; logic modules
 * import from here (or from the relevant data table) instead of using
 * literals (CONVENTIONS "Numbers from DESIGN.md are canonical").
 *
 * The flat names listed in DESIGN 2.10 / 7.3 are exported as-is. Everything
 * else is grouped per subsystem in frozen `as const` objects. Per-element
 * damage numbers (the DESIGN 2.5 table) live in `damage.ts`, enemy stats in
 * `enemies.ts`, status durations are mirrored into `status.ts`.
 *
 * Units: distances in arena units (1 u = UNIT_PX px), times in seconds unless
 * the name ends in `Ms`, angles in degrees only where the name says `Deg`.
 */

export { MAX_QUEUE } from './elements.js';

// ---------------------------------------------------------------- core loop (DESIGN 5.2)
export const TICK_RATE = 20;
export const TICK_DT = 1 / TICK_RATE;
export const SNAP_EVERY = 2;
export const DAMAGE_TICK_EVERY = 2;
export const DAMAGE_TICK_HZ = 10;
/** Storms tick every 10th sim tick (2 Hz). */
export const STORM_TICK_EVERY = 10;
/** Burning DoT ticks every 20th sim tick (1 Hz). */
export const BURN_TICK_EVERY = 20;
export const MAX_CATCHUP_TICKS = 5;
export const PROTOCOL_VERSION = 1;

// ---------------------------------------------------------------- player (DESIGN 2.5, 2.6, 4.2, 5.4)
export const PLAYER_HP = 1000;
export const PLAYER_SPEED = 6;
export const PLAYER_RADIUS = 0.45;
export const UNIT_PX = 32;
/** Seconds between casts (DESIGN 7.3 caster.ts). */
/** A released beam/spray/arc still applies this many damage ticks, so a quick click is a short burst rather than a wasted queue. */
export const MIN_CHANNEL_TICKS = 3;

/** Longest movement step one input may claim (client clamps to this too). */
export const MAX_INPUT_DT_SECONDS = 0.25;
/**
 * Server-side movement budget: inputs may only move a player for as much time
 * as has really passed (anti speed-hack), with this much slack for bursty arrival.
 */
export const MOVE_CREDIT_CAP_SECONDS = 0.5;

export const CAST_LOCK = 0.25;
/** Seconds of holding Force for a full projectile charge (DESIGN 2.5). */
export const CHARGE_TIME = 1.5;
/** Arcane damage taken when pressing Lightning while wet (DESIGN 2.7/2.8). */
export const SELF_SHOCK_DAMAGE = 250;
/** Knockback velocity decay, u/s per second (DESIGN 5.4 Body.knock). */
export const KNOCK_DECAY = 8;
/** Bodies clamp this far inside the arena walls (DESIGN 4.2). */
export const WALL_INSET = 0.5;
/** Players spawn on a ring of this radius around ARENA.spawn (DESIGN 4.2). */
export const PLAYER_SPAWN_RING = 1.5;
/** Largest level side in units (level.ts validation, the input clamp). The arena is 40 x 30. */
export const LEVEL_MAX_SIZE = 512;
/**
 * Enemy pathing on levels with walls (server sim/enemies/flow.ts): grid cell
 * in units, how much room a cell keeps from a wall, and how often a target's
 * field is rebuilt while it stands in one cell. Open levels (the arena) never
 * build one: enemies walk straight at you while they can see you.
 */
export const PATHING = { cell: 1, clearance: 0.45, rebuildSeconds: 0.5 } as const;
/**
 * Story mode (docs/spellweave-modes.md §1). countdown: seconds before the act
 * starts; checkpointRadius: how close a living wizard walks to a rune stone
 * to claim it; wipeSeconds: the pause after a party wipe before everyone
 * stands up at the last stone; endSeconds: the Book's last words before the
 * results; iceSeconds: how long a frozen deep-water cell stays walkable;
 * conductShare: the part of a lightning hit that runs through the water to
 * everyone else standing in it (story only, MODES.story.waterConducts).
 * Brambles: a burning cell lights its dry neighbours after
 * brambleSpreadSeconds and is ash after brambleBurnSeconds; a wet one stays
 * wet brambleWetSeconds, and a living shaman keeps every bramble within
 * shamanWetRadius wet. moltenSeconds: how long lava in the chasm stays
 * before it sinks away, unless cold sets it to stone.
 */
export const STORY = {
  countdown: 3,
  checkpointRadius: 1.8,
  // 3 until 2026-10-28; the wipe line and the end words can also be skipped with ready
  wipeSeconds: 2,
  endSeconds: 6,
  iceSeconds: 20,
  conductShare: 0.5,
  brambleSpreadSeconds: 0.5,
  brambleBurnSeconds: 1.5,
  brambleWetSeconds: 6,
  shamanWetRadius: 5,
  moltenSeconds: 12,
} as const;
/**
 * Player against player (docs/spellweave-modes.md §2). Damage one player deals
 * another is scaled by damageMul (friendly fire too, at the same scale).
 * A dead player respawns at the team base after respawnSeconds with
 * spawnShield seconds of shield (no damage taken). Magicks share one
 * cooldown per player: magickCooldown, quickCooldown for Haste and
 * Teleport. Thunder Bolt hits for boltDamage; Conflagration deals
 * conflagrationDamage and conflagrationStacks burn; Phoenix brings back
 * the caster's team at phoenixHpFrac. Beams and arcs aimed at players hit
 * where the caster saw them, rewinding at most rewindTicks; historyTicks of
 * positions are kept for it.
 */
export const PVP = {
  damageMul: 0.5,
  respawnSeconds: 5,
  spawnShield: 2,
  magickCooldown: 20,
  quickCooldown: 8,
  /** The Lesser Time Stop's Magick cooldown (weaves.ts LESSER_STOP). */
  stopCooldown: 60,
  boltDamage: 600,
  conflagrationDamage: 300,
  conflagrationStacks: 2,
  phoenixHpFrac: 0.5,
  rewindTicks: 4,
  historyTicks: 8,
} as const;
/**
 * Capture the flag (docs/spellweave-modes.md §2): first to `captures` or the
 * most when matchSeconds run out; a dropped flag goes home after
 * returnSeconds; flags are picked up, returned and captured within
 * touchRadius; the carrier moves at carrierSpeed.
 */
export const CTF = {
  captures: 3,
  matchSeconds: 600,
  returnSeconds: 15,
  touchRadius: 1.2,
  carrierSpeed: 0.85,
  // 5 until 2026-10-28; everyone pressing ready starts the match at once
  countdown: 3,
} as const;
/**
 * Mother Mire, act 1's boss (story mode, server enemies/mire.ts). Her HP is
 * the def's x the party multiplier. Phase 2 below phase2At of her HP, phase
 * 3 below phase3At. Slam: a slamTell wind-up on anyone within slamRange,
 * then slamDmg physical in slamRadius with slamKnockback, every slamCooldown
 * s. Every callEvery s she calls callCount spiders (x the party multiplier),
 * topping the room up to callCap of them alive (x the party multiplier too).
 * From phase 2 the chapel floods every floodEvery s for floodSeconds; she
 * sinks into her pool for the flood, healing healPerSecond of her max HP a
 * second, unless the pool is frozen: then she is stranded on the ice and
 * takes strandedMul damage for up to strandSeconds, then breaks free (once a
 * flood). From phase 3 a lit Everflame boils the flood:
 * boilSteam steam a second to everyone standing in it, and boilSunk of her
 * max HP a second while she hides in the boiling pool. A wizard's beam
 * crossing hers blows up crossMul times harder and the backlash hits her
 * for crossDirect arcane.
 */
export const MIRE = {
  phase2At: 0.6,
  phase3At: 0.25,
  slamRange: 2.6,
  slamTell: 0.7,
  slamDmg: 220,
  slamRadius: 3.2,
  slamKnockback: 9,
  // 5, 18 and 3 until the 2026-10-28 difficulty pass: a practised wizard took no damage from her at all
  slamCooldown: 4,
  callEvery: 14,
  callCount: 4,
  callCap: 12,
  floodEvery: 12,
  floodSeconds: 6,
  healPerSecond: 0.01,
  strandedMul: 2,
  strandSeconds: 6,
  boilSteam: 60,
  boilSunk: 0.03,
  crossMul: 3,
  crossDirect: 1500,
} as const;
/**
 * The Boiler Room's boiler (story mode), per hit (a spray or beam lands 10
 * times a second): water fills it by `fill` until full; once full, fire
 * heats it by `heat` (x hotMul from a packet of hotFire fire or more, a
 * bomber's blast), steam by steamHeat (and fills by steamFill), while water
 * cools it by `cool`, cold by coldCool and each Rain pulse by rainCool; a
 * Blizzard puts it out. Heat fades by `decay` a second. While heat is at
 * least `hot`, the pressure builds over the story room's `seconds`.
 */
export const BOILER = {
  radius: 1.05,
  fill: 0.05,
  heat: 0.04,
  hotFire: 100,
  hotMul: 3,
  steamHeat: 0.08,
  steamFill: 0.02,
  cool: 0.06,
  coldCool: 0.12,
  rainCool: 0.2,
  decay: 0.025,
  hot: 0.6,
  /** Gauge steps (a `boiler` gauge event each) across the pressure. */
  gaugeSteps: 6,
} as const;
/** Projectiles ignore their caster for this long after launch (DESIGN 2.8). */
export const PROJECTILE_SELF_GRACE = 0.3;

// ---------------------------------------------------------------- forms (DESIGN 2.5 form parameters, 2.8)
export const BEAM_RANGE = 14;
export const SPRAY_RANGE = 5;
export const ARC_RANGE = 7;
/** Beam rotation toward aim, degrees per second (x2 when hasted). */
export const BEAM_TURN_RATE = 90;

export const BEAM = {
  range: BEAM_RANGE,
  width: 0.3,
  /** Beam origin is offset this far from the caster centre along aim. */
  offset: 0.6,
  baseTicks: 11,
  ticksPerExtraBeamElement: 8,
  turnRateDeg: BEAM_TURN_RATE,
  hastedTurnRateDeg: 180,
  /** Lock-on keeps the current target while it stays inside this cone. */
  lockConeDeg: 15,
  walkSpeedMul: 0.5,
  /**
   * Each Arcane in a beam shoves what it hits along the beam at this many u/s (raw force, so a beam lands with
   * weight); only bodies that walk, so the training dummies and the story's objects stay where they stand.
   */
  arcanePush: 1,
} as const;

/**
 * Crossed beams (beamCross.ts). An opposite pair across the two beams
 * explodes at the crossing (DESIGN 2.8): `radius`, the summed packet x
 * `damageMul`, `knockback`, once per pair per `cooldown` s. Anything else
 * bounces: each beam reflects off the other and goes on for what is left of
 * its range (at least `bounceMin`), its damage or healing x `bounceMul`, or x
 * the highest `resonance` of an element both beams carry.
 */
export const BEAM_CROSS = {
  radius: 3.5,
  damageMul: 10,
  knockback: 12,
  cooldown: 0.5,
  bounceMin: 6,
  bounceMul: 1.5,
  resonance: { arcane: 2, steam: 2, lightning: 2, life: 1.75, fire: 1.75, cold: 1.75, mud: 1.75, spring: 1.75, water: 1.5 },
} as const;

/**
 * Sprays (sprayCone.ts). The cone the server hits with is the cone the client
 * draws: `range`, `halfAngleDeg`, and full strength out to `falloffFrom` of
 * the range, fading to `edgeScale` at the very edge (damage and push; the
 * statuses always land).
 */
export const SPRAY = {
  range: SPRAY_RANGE,
  halfAngleDeg: 20,
  baseTicks: 6,
  ticksPerElement: 2,
  /** Each Water in the queue adds this much range. */
  waterRangeBonus: 1,
  falloffFrom: 0.6,
  edgeScale: 0.7,
} as const;

export const ARC = { range: ARC_RANGE, chainRange: 4, ticks: 8 } as const;

/** Lava boulders leave a burning pool where they land. */
export const LAVA_POOL = {
  baseRadius: 1.6,
  radiusPerLava: 0.4,
  baseLifetime: 4,
  lifetimePerLava: 1,
  /** Fire damage per pool tick (2 Hz), per Lava. */
  firePerTick: 25,
  tickSeconds: 0.5,
} as const;

/**
 * The thrown rock (rockThrow.ts). How long Force is held sets the throw:
 * from a tap's lob (`minRange` u at `minSpeed` u/s, a `minRadius` pebble) to a
 * full charge (`range` u at `speed` u/s, a `maxRadius` boulder), linear in the
 * charge. It is held at `handHeight` u in front of the caster while it grows,
 * and arcs `arcBase` + `arcPerUnit` x range u high on the way. The server takes
 * the client's own hold time when it is within `holdSlack` s of what it
 * measured, so the client's predicted rock flies the same throw.
 */
export const BOULDER = {
  speed: 24,
  range: 15,
  minSpeed: 10,
  minRange: 2.5,
  minRadius: 0.2,
  maxRadius: 0.38,
  handHeight: 0.9,
  arcBase: 0.35,
  arcPerUnit: 0.05,
  holdSlack: 0.25,
  /** direct x (1 + directChargeBonus x charge) */
  directChargeBonus: 3.4,
  blastRadius: 1.5,
  /** Each Water in the payload adds this much blast radius. */
  waterBlastBonus: 0.5,
  /** Arcane or Life in the payload adds this much blast radius. */
  beamElementBlastBonus: 1,
  walkSpeedMulWhileCharging: 0.5,
} as const;

export const SHARDS = {
  perIce: 3,
  damage: 40,
  range: 12,
  spreadMaxDeg: 40,
  spreadMinDeg: 10,
  speedMin: 22,
  speedMax: 32,
} as const;

export const NOVA = { baseRadius: 3, radiusPerExtraElement: 0.75 } as const;
export const QUAKE = { baseRadius: 3 } as const;

export const BARRIER = {
  hp: 500,
  decayPerSecond: 10,
  arcDeg: 120,
  arcRadius: 2.5,
  ringRadius: 2.5,
  lineLength: 4,
  thickness: 0.3,
} as const;

export const WALL = {
  earthLifetime: 20,
  earthHpBase: 1500,
  earthHpPerEarth: 500,
  iceLifetime: 6,
  iceHpBase: 600,
  iceHpPerIce: 200,
  /** Ice wall rise damage per Ice (physical). */
  iceRisePerIce: 275,
} as const;

export const MINES = {
  lifetime: 60,
  maxPerOwner: 24,
  triggerRadius: 0.6,
  blastRadius: 2.0,
  blastDelay: 0.1,
  chainRadius: 2,
  arcRadius: 3,
  ringRadius: 3,
  lineSpacing: 1.2,
  countArc: 4,
  countRing: 8,
  countLine: 3,
  arcaneDamage: 225,
  arcaneKnockback: 10,
  lifeHeal: 599,
} as const;

export const STORM = {
  baseLifetime: 8,
  lifetimePerElement: 2,
  thickness: 1.0,
  tickHz: 2,
  fire: 20,
  lightning: 65,
  steam: 40,
} as const;

export const BUBBLE = { hp: 500, decayPerSecond: 10 } as const;
export const AURA = { baseLifetime: 15, lifetimePerExtraElement: 5, baseRadius: 3 } as const;
export const ARMOR = { lifetime: 10, rockAbsorb: 1500, rockSlow: 0.3, iceAbsorb: 800, iceSlow: 0.15 } as const;

/** An imbued staff carries its spell for this many swings, then reverts to the bare staff. */
export const IMBUE_SWINGS = 3;

export const MELEE = {
  swingTime: 0.35,
  swingArcDeg: 100,
  swingRadius: 1.6,
  thrustLength: 2.5,
  fissureLength: 5,
  slashArcDeg: 60,
  slashRadius: 2,
  baseDamage: 100,
  knockback: 4,
  /** Ice slash: 180 x stackFluid(n) physical. */
  iceSlashBase: 180,
} as const;

export const WIND = { coneDeg: 60, radius: 3, knockback: 8 } as const;

/** Life self-cast (DESIGN 2.6). */
export const SELF_CAST = { heal: 179, hotSeconds: 3 } as const;

// ---------------------------------------------------------------- statuses (DESIGN 2.7)
/** Status durations in seconds; `status.ts` re-exports this as STATUS_DURATION. */
export const STATUS_SECONDS = {
  wet: 12,
  burning: 6,
  chilled: 6,
  frozen: 4,
  shocked: 0.4,
  knockdown: 1.2,
  hasted: 10,
  slowed: 15,
  /** Mud: stuck in it. Short, and burning dries it off. */
  mired: 4,
  /** Ash: an enemy loses its target. */
  blinded: 2,
} as const;

export const BURN = {
  dpsPerStack: 20,
  maxStacks: 6,
  /** Enemies with this many stacks panic. */
  panicStacks: 4,
  /** Wet removes this many burn stacks per second. */
  wetDecayPerSecond: 1,
} as const;

export const STATUS_EFFECT = {
  chillMoveMul: 0.6,
  chillCastMul: 0.6,
  chillBeamTurnMul: 0.5,
  hasteMoveMul: 2,
  hasteBeamTurnMul: 2,
  slowedMul: 0.5,
  /** Mired (mud) movement multiplier. */
  miredMul: 0.45,
} as const;

export const DAMAGE_MUL = {
  wetLightning: 2,
  armoredLightning: 1.5,
  frozen: 3,
  armoredPhysical: 0.5,
  armoredFire: 0,
} as const;

// ---------------------------------------------------------------- magicks (DESIGN 2.9)
export const MAGICK = {
  reviveHp: 100,
  reviveDistance: 4,
  reviveDelay: 0.25,
  hasteSeconds: 10,
  teleportDistance: 8,
  boltDamage: 5000,
  boltSplash: 500,
  boltSplashRadius: 1,
  boltSearchRadius: 6,
  boltAhead: 8,
  rainSeconds: 17.5,
  rainPeriod: 2,
  timewarpSeconds: 15,
  conflagrationDamage: 600,
  conflagrationRadius: 8,
  conflagrationStacks: 3,
  phoenixDamage: 400,
  phoenixRadius: 4,
  phoenixStacks: 2,
  blizzardSeconds: 10,
  blizzardPeriod: 2,

  // The five added after v1 (2026-10-27; not in DESIGN). Placed Magicks land this far along the aim, clamped to the arena.
  /** Meteor Shower: `meteorCount` rocks, one every `meteorEvery` s, each landing `meteorWarn` s after its shadow appears. */
  meteorAhead: 7,
  meteorCount: 7,
  meteorSpread: 3.5,
  meteorEvery: 0.35,
  meteorWarn: 0.9,
  meteorRadius: 1.6,
  meteorEarth: 260,
  meteorFire: 120,
  meteorKnockback: 6,
  /** Tornado: walks the aim, bounces off the walls, flings what it touches (a body once per `tornadoRecatch` s). */
  /** Far enough that the funnel (radius + a wizard's) does not catch its caster as it forms. */
  tornadoAhead: 2.2,
  tornadoSeconds: 6,
  tornadoSpeed: 3.2,
  tornadoRadius: 1.1,
  tornadoDamage: 60,
  tornadoThrow: 9,
  tornadoRecatch: 1.5,
  /** The nova packet of what it was fed, times this, rides every fling. */
  tornadoFeedScale: 0.5,
  /** Thunder Storm: a bolt every `stormEvery` s on an enemy under the cloud, wet ones first. */
  stormAhead: 7,
  stormSeconds: 8,
  stormRadius: 6,
  stormEvery: 0.7,
  stormDamage: 350,
  stormSplash: 150,
  stormSplashRadius: 1,
  /** Vortex: pull for `vortexSeconds`, then burst for base + per body caught (capped). Players are pulled more gently. */
  vortexAhead: 7,
  vortexSeconds: 3.5,
  /** Less than vortexAhead, so its caster starts outside the pull (a friend standing nearer is fair game). */
  vortexRadius: 6,
  vortexPull: 4.5,
  vortexPlayerPull: 2.5,
  vortexBlastRadius: 2.5,
  vortexBase: 250,
  vortexPerBody: 120,
  vortexBodyCap: 10,
  vortexKnockback: 8,
  /** Summon Elemental: one per caster, walks to the nearest enemy and slams (enemies only). */
  elementalAhead: 3,
  elementalSeconds: 18,
  elementalSpeed: 2.6,
  elementalRadius: 0.8,
  elementalSlamEvery: 1.4,
  elementalSlamRadius: 2,
  elementalSlamDamage: 300,
  elementalSlamKnockback: 5,
  elementalFeedScale: 0.6,
  /** Fed Life, a slam heals the players inside it this much. */
  elementalHeal: 150,
  /** A tornado or elemental holds at most this many fed elements; a new one pushes the oldest out, an opposite cancels. */
  feedMax: 3,
} as const;

/**
 * Boss waves and the jotunn's two phases (added 2026-10-27, not in DESIGN).
 * Tells are seconds from the warning (drawn on the ground) to the hit.
 */
export const BOSS = {
  every: 5,
  /**
   * Jotunn HP on boss waves 1, 2, 3+ (wave 5, 10, 15+), before the player multiplier, the difficulty and endless
   * scaling. Was 8000 / 12000 / 14000 until the 2026-10-28 difficulty pass: the first boss stays a lesson, the
   * later ones last long enough to need a plan.
   */
  hp: [8000, 16000, 24000],
  /** Phase 2 starts when HP falls to this fraction of the max. */
  rageAt: 0.5,
  /** The rage roar: it stands this long, shoving everyone within the radius away. */
  rageStagger: 1.4,
  rageShove: 9,
  rageShoveRadius: 4.5,
  /** Phase 2 walks faster and recovers its attacks faster. */
  rageSpeedMul: 1.25,
  rageCooldownMul: 1.35,
  fissureTell: 0.75,
  fissureTellRage: 0.55,
  /** Phase 2 fissures fan out: two more lines this many degrees to each side. */
  fanDeg: 28,
  /** Glacier Slam (phase 2): the frost ring grows for `slamTell` s, then cold + chill (frozen if wet) and a shove. */
  slamTell: 1.2,
  slamRadius: 4.5,
  slamCold: 350,
  slamKnockback: 8,
  slamFirst: 3,
  slamEvery: 7,
  /** It starts a slam only with a player this close. */
  slamReach: 6,
} as const;

// ---------------------------------------------------------------- mode, waves, scoring (DESIGN 4)
/**
 * The waits (DESIGN 4.1 had 5 / 12 / 15 s). Cut 2026-10-28 after Bennett found them long "for what seems like no
 * reason": 3 s countdown, 8 s breather (still room to weave or stash), 10 s of results. Every one of them can also
 * be skipped: once everyone in the room presses ready (`next`), it ends at once, so alone it is one press.
 */
export const COUNTDOWN_SECONDS = 3;
export const BREATHER_SECONDS = 8;
export const SPAWN_INTERVAL = 0.4;
export const RESULTS_SECONDS = 10;
export const SCRIPTED_WAVES = 12;
/** Reaching this wave ends the run (DESIGN 4.1). */
export const FINAL_WAVE = 30;
export const ENDLESS_HP_SCALE_PER_WAVE = 0.15;
/** Endless waves draw their composition from this inclusive range. */
export const ENDLESS_SOURCE_WAVES = { min: 7, max: 11 } as const;
/** Enemy count multiplier by player count (index = players - 1). */
export const PLAYER_COUNT_MULT: readonly [number, number, number, number] = [1, 1.6, 2.1, 2.5];
export const AUTO_REVIVE_HP_FRAC = 0.3;
export const DUMMY = { hp: 5000, regenDelaySeconds: 3 } as const;
export const ENEMY_SEPARATION_RADIUS = 0.6;
/** Seconds a dead wizard waits in the lobby sandbox before respawning (DESIGN 4.1 "infinite respawn"; no number given). */
export const LOBBY_RESPAWN_SECONDS = 3;
/** Spawned enemies are offset up to this far along their gate's wall so a group does not stack (DESIGN 4.4). */
export const SPAWN_JITTER = 0.6;

export const SCORE = {
  kill: 10,
  waveClearPerWave: 100,
  bossKill: 1000,
  frozenKill: 25,
  wetLightningKill: 15,
  bomberChain: 20,
  mergeKill: 30,
  crossKill: 50,
} as const;

// ---------------------------------------------------------------- rooms and net (DESIGN 5.1, 5.2, 5.7)
export const MAX_PLAYERS = 4;
export const MAX_ROOMS = 200;
export const ROOM_GC_SECONDS = 60;
export const RECONNECT_SECONDS = 10;
export const ROOM_CODE_LENGTH = 5;
/** Unambiguous uppercase alphabet: no I or O (DESIGN 5.1). */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const MAX_ACTS_PER_INPUT = 32;
export const MAX_NAME_LENGTH = 24;
export const MAX_CHAT_LENGTH = 80;
export const MAX_TOKEN_LENGTH = 128;

export const NET = {
  /** Inbound messages per second per socket before dropping. */
  inputRateLimit: 40,
  clientInputHz: 20,
  maxPayloadBytes: 16384,
  badMsgLimit: 5,
  badMsgWindowSeconds: 10,
  closeCodeBadMsg: 4000,
  heartbeatMs: 5000,
  heartbeatTimeoutMs: 15000,
  pingIntervalMs: 2000,
  reconnectBackoffMinMs: 500,
  reconnectBackoffMaxMs: 8000,
  snapshotSizeGuardBytes: 60 * 1024,
  snapshotEventCap: 64,
} as const;

// ---------------------------------------------------------------- client (DESIGN 5.6, 6.1, 6.2)
export const INTERP_DELAY_MS = 150;
export const CLIENT = {
  interpDelayMs: INTERP_DELAY_MS,
  extrapolateMaxMs: 200,
  correctionBlendMs: 100,
  /** Prediction error above this snaps instead of blending. */
  snapErrorUnits: 2,
  clockEmaAlpha: 0.1,
  /** Local beams follow local aim for this long before trusting the server. */
  localBeamAimMs: 100,
  rmbStopDistance: 0.3,
  cameraLookahead: 0.25,
  shakeMaxPx: 6,
  particlePool: 2000,
  maxVoices: 24,
} as const;
